import { API_BASE_URL } from './client';

const parseError = async (response, fallback) => {
  const error = await response.json().catch(() => ({}));
  const d = error.detail;
  if (Array.isArray(d)) {
    return d.map((x) => (typeof x === 'string' ? x : x.msg || JSON.stringify(x))).join('; ');
  }
  return (typeof d === 'string' && d) || fallback;
};

export const getBots = async (keycloak, assistantId = null) => {
  if (!keycloak?.token) throw new Error('Keycloak not initialized or no token available');
  const params = new URLSearchParams();
  if (assistantId) params.append('assistant_id', assistantId);
  const qs = params.toString();
  const response = await fetch(`${API_BASE_URL}/bots/${qs ? `?${qs}` : ''}`, {
    headers: { Authorization: `Bearer ${keycloak.token}` },
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to fetch bots'));
  return response.json();
};

export const getBot = async (keycloak, botId) => {
  if (!keycloak?.token) throw new Error('Keycloak not initialized or no token available');
  const response = await fetch(`${API_BASE_URL}/bots/${botId}/`, {
    headers: { Authorization: `Bearer ${keycloak.token}` },
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to fetch bot'));
  return response.json();
};

export const createBot = async (keycloak, botData) => {
  if (!keycloak?.token) throw new Error('Keycloak not initialized or no token available');
  const response = await fetch(`${API_BASE_URL}/bots/`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(botData),
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to create bot'));
  return response.json();
};

export const updateBot = async (keycloak, botId, botData) => {
  if (!keycloak?.token) throw new Error('Keycloak not initialized or no token available');
  const response = await fetch(`${API_BASE_URL}/bots/${botId}/`, {
    method: 'PUT',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(botData),
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to update bot'));
  return response.json();
};

export const deleteBot = async (keycloak, botId) => {
  if (!keycloak?.token) throw new Error('Keycloak not initialized or no token available');
  const response = await fetch(`${API_BASE_URL}/bots/${botId}/`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${keycloak.token}` },
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to delete bot'));
  return response.json();
};

export const enableBot = async (keycloak, botId) => {
  if (!keycloak?.token) throw new Error('Keycloak not initialized or no token available');
  const response = await fetch(`${API_BASE_URL}/bots/${botId}/enable/`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${keycloak.token}` },
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to enable bot'));
  return response.json();
};

export const disableBot = async (keycloak, botId) => {
  if (!keycloak?.token) throw new Error('Keycloak not initialized or no token available');
  const response = await fetch(`${API_BASE_URL}/bots/${botId}/disable/`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${keycloak.token}` },
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to disable bot'));
  return response.json();
};

export const getBotStatus = async (keycloak, botId) => {
  if (!keycloak?.token) throw new Error('Keycloak not initialized or no token available');
  const response = await fetch(`${API_BASE_URL}/bots/${botId}/status/`, {
    headers: { Authorization: `Bearer ${keycloak.token}` },
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to fetch bot status'));
  return response.json();
};

export const getBotLogs = async (keycloak, botId) => {
  if (!keycloak?.token) throw new Error('Keycloak not initialized or no token available');
  const response = await fetch(`${API_BASE_URL}/bots/${botId}/logs/`, {
    headers: { Authorization: `Bearer ${keycloak.token}` },
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to fetch bot logs'));
  return response.json();
};

export const telegramLoginStart = async (keycloak, botId, phone) => {
  if (!keycloak?.token) throw new Error('Keycloak not initialized or no token available');
  const response = await fetch(`${API_BASE_URL}/bots/${botId}/telegram/login/start/`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ phone }),
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to start Telegram login'));
  return response.json();
};

export const telegramLoginCode = async (keycloak, botId, code) => {
  if (!keycloak?.token) throw new Error('Keycloak not initialized or no token available');
  const response = await fetch(`${API_BASE_URL}/bots/${botId}/telegram/login/code/`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ code }),
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to submit login code'));
  return response.json();
};

export const telegramLogin2FA = async (keycloak, botId, password) => {
  if (!keycloak?.token) throw new Error('Keycloak not initialized or no token available');
  const response = await fetch(`${API_BASE_URL}/bots/${botId}/telegram/login/2fa/`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ password }),
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to submit 2FA password'));
  return response.json();
};

export const getTelegramAccount = async (keycloak, botId) => {
  if (!keycloak?.token) throw new Error('Keycloak not initialized or no token available');
  const response = await fetch(`${API_BASE_URL}/bots/${botId}/telegram/account/`, {
    headers: { Authorization: `Bearer ${keycloak.token}` },
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to fetch Telegram account'));
  return response.json();
};

export const disconnectTelegram = async (keycloak, botId) => {
  if (!keycloak?.token) throw new Error('Keycloak not initialized or no token available');
  const response = await fetch(`${API_BASE_URL}/bots/${botId}/telegram/disconnect/`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${keycloak.token}` },
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to disconnect Telegram'));
  return response.json();
};

export const getBotConversations = async (keycloak, botId) => {
  if (!keycloak?.token) throw new Error('Keycloak not initialized or no token available');
  const response = await fetch(`${API_BASE_URL}/bots/${botId}/conversations/`, {
    headers: { Authorization: `Bearer ${keycloak.token}` },
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to fetch bot conversations'));
  return response.json();
};

export const getBotConversationMessages = async (keycloak, botId, platformChatId) => {
  if (!keycloak?.token) throw new Error('Keycloak not initialized or no token available');
  const encoded = encodeURIComponent(platformChatId);
  const response = await fetch(`${API_BASE_URL}/bots/${botId}/conversations/${encoded}/messages/`, {
    headers: { Authorization: `Bearer ${keycloak.token}` },
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to fetch conversation messages'));
  return response.json();
};

export const deleteBotMessage = async (keycloak, botId, messageId) => {
  if (!keycloak?.token) throw new Error('Keycloak not initialized or no token available');
  const response = await fetch(`${API_BASE_URL}/bots/${botId}/messages/${messageId}/`, {
    method: 'DELETE',
    headers: { Authorization: `Bearer ${keycloak.token}` },
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to delete message'));
};

export const searchBotConversations = async (keycloak, botId, q, limit = 20) => {
  if (!keycloak?.token) throw new Error('Keycloak not initialized or no token available');
  const params = new URLSearchParams({ q, limit: String(limit) });
  const response = await fetch(`${API_BASE_URL}/bots/${botId}/conversations/search/?${params}`, {
    headers: { Authorization: `Bearer ${keycloak.token}` },
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to search conversations'));
  return response.json();
};

export const getBotSessions = async (keycloak, botId) => {
  if (!keycloak?.token) throw new Error('Keycloak not initialized or no token available');
  const response = await fetch(`${API_BASE_URL}/bots/${botId}/sessions/`, {
    headers: { Authorization: `Bearer ${keycloak.token}` },
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to fetch bot sessions'));
  return response.json();
};

export const getBotSessionMessages = async (keycloak, botId, sessionId) => {
  if (!keycloak?.token) throw new Error('Keycloak not initialized or no token available');
  const response = await fetch(`${API_BASE_URL}/bots/${botId}/sessions/${sessionId}/messages/`, {
    headers: { Authorization: `Bearer ${keycloak.token}` },
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to fetch session messages'));
  return response.json();
};

export const getBotRefusedMessages = async (keycloak, botId) => {
  if (!keycloak?.token) throw new Error('Keycloak not initialized or no token available');
  const response = await fetch(`${API_BASE_URL}/bots/${botId}/refused/`, {
    headers: { Authorization: `Bearer ${keycloak.token}` },
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to fetch refused messages'));
  return response.json();
};

export const draftRefusedAnswer = async (keycloak, botId, messageId, question) => {
  if (!keycloak?.token) throw new Error('Keycloak not initialized or no token available');
  const response = await fetch(`${API_BASE_URL}/bots/${botId}/refused/${messageId}/draft-answer/`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ question }),
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to draft answer'));
  return response.json();
};

export const addCollectionText = async (keycloak, collectionId, { title, content }) => {
  if (!keycloak?.token) throw new Error('Keycloak not initialized or no token available');
  const response = await fetch(`${API_BASE_URL}/data-collections/collections/${collectionId}/text/`, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${keycloak.token}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ title, content }),
  });
  if (!response.ok) throw new Error(await parseError(response, 'Failed to add text to collection'));
  return response.json();
};
